/* Staff accounts: the gym manages its own logins.
 *
 * Owner and Manager only. An Owner may hand out every gym role except the Acrostic HQ
 * seat; a Manager lives inside {Support Coach, Coach} and cannot see past it. Nobody
 * can lock themselves out, and the gym's last Owner cannot be removed or demoted.
 *
 * THIS FILE IS THE UX HALF ONLY. Every rule is enforced again in the source, and the
 * controls below just avoid OFFERING a seat that would be refused. Every refusal that
 * does get through is shown verbatim rather than swallowed, because a rule the
 * product will not explain is a rule the owner will fight.
 */
import { useState } from 'react';
import type { Permission, Role } from '@acrostic/model';
import { PERMISSIONS, PERMISSION_LABELS, isDefaultFor, permissionsOf } from '@acrostic/model';
import { useAsync, useSource } from '../data/SourceProvider.js';
import type { Account } from '../data/contract.js';
import { ACCOUNT_ROLES, grantableBy } from '../data/SeedSource.js';
import { NoneRow } from '../components/Panel.js';
import { Field, Modal } from '../components/Modal.js';
import { Avatar } from '../components/Avatar.js';
import { useToast } from '../components/Toast.js';
import type { ScreenProps } from '../App.js';

const LABEL = (k: Role): string => ACCOUNT_ROLES.find((r) => r.key === k)?.label ?? String(k).replace(/_/g, ' ');

/* What is different about them, in words, shortest first. "Also sees plans and prices"
   is a sentence somebody can act on; a list of eight checkboxes is not. */
function describe(a: Account): string {
  const seat = permissionsOf(a.roles, null);
  const extra = a.permissions.filter((p) => !seat.includes(p));
  const missing = seat.filter((p) => !a.permissions.includes(p));
  const name = (p: Permission) => PERMISSION_LABELS[p].name.toLowerCase();
  const bits: string[] = [];
  if (extra.length) bits.push('also ' + extra.map(name).join(', '));
  if (missing.length) bits.push('cannot ' + missing.map(name).join(', '));
  return bits.join('; ') || 'the same as the seat';
}

export function Accounts({ user }: ScreenProps) {
  const source = useSource();
  const toast = useToast();
  const [epoch, setEpoch] = useState(0);
  const [editing, setEditing] = useState<Account | 'new' | null>(null);
  const [shown, setShown] = useState<{ name: string; password: string } | null>(null);
  const [permsFor, setPermsFor] = useState<Account | null>(null);
  const { data, error } = useAsync(() => source.getAccounts(), [source, epoch]);

  const grantable = grantableBy(user);

  if (error) return <NoneRow>Could not read the accounts. {error.message}</NoneRow>;
  if (!data) return <NoneRow>Reading…</NoneRow>;

  if (!grantable.length) {
    return (
      <section className="panel">
        <NoneRow>Staff accounts are an Owner and Manager screen.</NoneRow>
      </section>
    );
  }

  async function remove(a: Account) {
    try {
      await source.removeAccount(a.id);
      toast(`${a.name} removed`, 'good');
      setEpoch((n) => n + 1);
    } catch (e) {
      toast((e as Error).message, 'bad');
    }
  }

  return (
    <>
      <section className="panel">
        <header>
          <h2 className="t-heading-h3">Staff accounts</h2>
          <span className="eyebrow t-label-xsmall">{data.length} logins</span>
          <span className="spacer" />
          <button type="button" className="btn primary t-label-small" onClick={() => setEditing('new')}>
            + New account
          </button>
        </header>

        <table className="table">
          <thead>
            <tr className="t-label-small"><th>Person</th><th>Seats</th><th /></tr>
          </thead>
          <tbody>
            {data.map((a) => (
              <tr key={a.id} style={{ cursor: 'default' }}>
                <td>
                  <Avatar name={a.name} photoUrl={a.photoUrl} size="sm" />
                  <span className="namecell">
                    <span className="t-label-large">
                      {a.name}
                      {a.isSelf && <span className="badge t-label-xsmall">you</span>}
                    </span>
                    <span className="t-caption muted">{a.email}</span>
                  </span>
                </td>
                <td>
                  {a.roles.map((r) => <span className="badge t-label-xsmall" key={r}>{LABEL(r)}</span>)}
                  {/* SAY WHEN SOMEBODY IS NOT WHAT THEIR SEAT SAYS. A staff list where
                      one Coach can see prices and the others cannot, and it does not
                      show on the row, is a list that lies quietly. */}
                  {a.custom && (
                    <div className="t-caption muted">
                      Not the usual {a.roles.map(LABEL).join(' + ')}: {describe(a)}
                    </div>
                  )}
                  {a.lastOwner && (
                    <div className="t-caption muted">The gym&rsquo;s only Owner. Cannot be removed or demoted.</div>
                  )}
                </td>
                <td className="right" style={{ whiteSpace: 'nowrap' }}>
                  {a.manageable ? (
                    <>
                      <button type="button" className="btn t-label-small" onClick={() => setEditing(a)}>Seats</button>{' '}
                      <button
                        type="button" className="btn t-label-small"
                        onClick={() => setPermsFor(a)}
                      >What they can do</button>{' '}
                      <button
                        type="button" className="btn t-label-small"
                        onClick={() => void source.resetAccountPassword(a.id).then((p) => setShown({ name: a.name, password: p }))}
                      >Reset password</button>{' '}
                      <button
                        type="button" className="btn t-label-small"
                        disabled={a.isSelf || a.lastOwner}
                        onClick={() => void remove(a)}
                      >Remove</button>
                    </>
                  ) : (
                    <span className="t-caption muted">Above your seat</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      {permsFor && (
        <Permissions
          a={permsFor}
          mine={permissionsOf(user.roles, user.permissions ?? null)}
          onClose={() => setPermsFor(null)}
          onSaved={() => { setPermsFor(null); setEpoch((n) => n + 1); toast('Saved', 'good'); }}
        />
      )}

      {editing && (
        <Editor
          a={editing === 'new' ? null : editing}
          grantable={grantable}
          onClose={() => setEditing(null)}
          onSaved={(pw) => { setEditing(null); setEpoch((n) => n + 1); if (pw) setShown(pw); }}
        />
      )}

      {shown && <PasswordOnce {...shown} onClose={() => setShown(null)} />}
    </>
  );
}

function Editor({ a, grantable, onClose, onSaved }: {
  a: Account | null; grantable: Role[]; onClose: () => void;
  onSaved: (pw: { name: string; password: string } | null) => void;
}) {
  const source = useSource();
  const toast = useToast();
  const [name, setName] = useState(a?.name ?? '');
  const [email, setEmail] = useState(a?.email ?? '');
  const [roles, setRoles] = useState<Role[]>(a?.roles ?? []);
  const [busy, setBusy] = useState(false);

  async function save() {
    setBusy(true);
    try {
      if (a) {
        await source.setAccountRoles(a.id, roles);
        toast('Seats saved', 'good');
        onSaved(null);
      } else {
        const made = await source.createAccount({ name, email, roles });
        toast(`${made.account.name} can sign in now`, 'good');
        onSaved({ name: made.account.name, password: made.password });
      }
    } catch (e) {
      toast((e as Error).message, 'bad');
      setBusy(false);
    }
  }

  return (
    <Modal
      title={a ? 'Seats · ' + a.name : 'New account'}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn t-label-medium" onClick={onClose}>Cancel</button>
          <button type="button" className="btn primary t-label-medium" disabled={busy} onClick={() => void save()}>
            {a ? 'Save seats' : 'Create account'}
          </button>
        </>
      }
    >
      {!a && (
        <>
          <Field label="Name">
            <input className="field" value={name} onChange={(e) => setName(e.target.value)} autoFocus />
          </Field>
          <Field label="Email" hint="This is what they sign in with.">
            <input className="field" type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
          </Field>
        </>
      )}

      <div className="t-label-small">Seats</div>
      {/* Only the seats this person may hand out. The source refuses the rest as
          well, so this list is a courtesy rather than the enforcement. */}
      {ACCOUNT_ROLES.filter((r) => grantable.includes(r.key)).map((r) => (
        <label className="togglerow" key={r.key}>
          <input
            type="checkbox" checked={roles.includes(r.key)}
            onChange={(e) => setRoles((cur) =>
              e.target.checked ? [...cur, r.key] : cur.filter((x) => x !== r.key))}
          />
          <span>
            <span className="t-label-large">{r.label}</span>
            <span className="t-body-small muted">{r.desc}</span>
          </span>
        </label>
      ))}
      {a?.lastOwner && (
        <p className="notice warn t-body-small">
          This is the gym&rsquo;s only Owner. Taking the Owner seat away would leave nobody
          who can manage accounts, so it is refused.
        </p>
      )}
    </Modal>
  );
}

/* Shown ONCE, and in the demo it is the same word for everybody, which the dialog
   says rather than printing a generated string the demo cannot enforce. */
function PasswordOnce({ name, password, onClose }: { name: string; password: string; onClose: () => void }) {
  return (
    <Modal
      title={'Password for ' + name} onClose={onClose}
      footer={<button type="button" className="btn primary t-label-medium" onClick={onClose}>Done</button>}
    >
      <p className="t-body-small">Give them this, and ask them to change it after their first sign-in.</p>
      <input className="field pw" readOnly value={password} onFocus={(e) => e.target.select()} />
      <p className="t-caption muted">
        Every account in this demo signs in with the same word. On a real gym this is a
        generated password shown once and never again.
      </p>
    </Modal>
  );
}

/* WHAT ONE PERSON CAN DO.
 *
 * A seat is a preset, not a ceiling: this is where an Owner says "this coach also does
 * the billing" without handing over the whole console. Two things the screen has to be
 * honest about, and both are why this is not just eight checkboxes:
 *
 *   A permission you do not hold yourself is shown and DISABLED rather than hidden.
 *   Hiding it reads as "that does not exist"; disabling it with a reason reads as
 *   "not yours to give", which is the truth and is actionable.
 *
 *   "Back to the seat's defaults" is a button, not the absence of choices. It is how
 *   somebody is put back on the preset and kept tracking it, and without it the only
 *   way back is to tick what you believe the default to be.
 */
function Permissions(
  { a, mine, onClose, onSaved }:
  { a: Account; mine: Permission[]; onClose: () => void; onSaved: () => void },
) {
  const source = useSource();
  const toast = useToast();
  const [set, setSet] = useState<Permission[]>(a.permissions);
  const [busy, setBusy] = useState(false);
  const seat = permissionsOf(a.roles, null);

  const toggle = (p: Permission) =>
    setSet((s) => (s.includes(p) ? s.filter((x) => x !== p) : [...s, p]));

  const save = async (permissions: Permission[] | null) => {
    setBusy(true);
    try {
      await source.setAccountPermissions(a.id, permissions);
      onSaved();
    } catch (e) {
      /* Verbatim. A rule the product will not explain is a rule the owner will fight. */
      toast(e instanceof Error ? e.message : 'That was refused', 'bad');
      setBusy(false);
    }
  };

  return (
    <Modal
      title={'What they can do · ' + a.name}
      onClose={onClose}
      footer={
        <>
          <button
            type="button" className="btn t-label-medium" disabled={busy}
            onClick={() => void save(null)}
          >Back to the seat&rsquo;s defaults</button>{' '}
          <button
            type="button" className="btn primary t-label-medium" disabled={busy}
            onClick={() => void save(set)}
          >Save</button>
        </>
      }
    >
      <p className="t-body-small muted">
        {a.name} sits in the {a.roles.map(LABEL).join(' + ')} seat. That seat decides
        this list unless you change it here, and changing it here changes it for{' '}
        {a.name} alone.
      </p>

      <div className="rows" style={{ marginTop: 12 }}>
        {PERMISSIONS.map((p) => {
          const yours = mine.includes(p);
          const on = set.includes(p);
          return (
            <label
              key={p}
              className="row"
              style={{ alignItems: 'flex-start', cursor: yours ? 'pointer' : 'default' }}
            >
              <input
                type="checkbox" checked={on} disabled={!yours || busy}
                onChange={() => toggle(p)}
                style={{ marginTop: 4, marginRight: 10 }}
              />
              <span className="who">
                <span className="n t-label-large">{PERMISSION_LABELS[p].name}</span>
                <span className="s t-caption muted">{PERMISSION_LABELS[p].help}</span>
                {!yours && (
                  <span className="s t-caption muted">
                    Not yours to give: your own seat does not have it.
                  </span>
                )}
                {yours && seat.includes(p) !== on && (
                  <span className="s t-caption muted">
                    {on ? 'Added' : 'Removed'} for {a.name}, against the {a.roles.map(LABEL).join(' + ')} seat.
                  </span>
                )}
              </span>
            </label>
          );
        })}
      </div>

      {isDefaultFor(a.roles, set) && (
        <p className="t-caption muted" style={{ marginTop: 12 }}>
          This is exactly what the seat means. Saving it is harmless, but
          &ldquo;back to the seat&rsquo;s defaults&rdquo; is the one that keeps tracking
          the seat if it ever changes.
        </p>
      )}
    </Modal>
  );
}
